/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// import React
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import FormHelperText from '@mui/material/FormHelperText';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Icon from "@mui/material/Icon";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import propertiesData from 'layouts/propertyLeases/data/propertyLeasesData';
import EditLeases from "examples/EditLeaseDialog";

import LinearProgress from '@mui/material/LinearProgress';
import * as XLSX from 'xlsx'; // Use named imports for xlsx
import PropertyService from "services/property-service"; // Import PropertyService
import LeaseInvoiceService from "services/lease-invoice-service";

// Helpers
const ylcId = "2800c706-805e-4cd9-9a64-90df329827b3";
const yl3Id = "c96a2dcb-58c7-4008-9b0d-43c3ee073822";
const gibId = "0ac1e107-d227-45db-a9d6-ca9afd806331";

function Payments () {
  const [errors, setErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tenantCode, setTenantCode] = useState('');
  const [inputValue, setInputValue] = useState("");
  const [filter, setFilter] = useState('');
  const [isShow, setIsShow] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [errorEdit, setErrorEdit] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [pageCustom, setPageCustom] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedValue, setSelectedValue] = useState(gibId);
  const [excelData, setExcelData] = useState([]);
  const [isExcelPopupOpen, setIsExcelPopupOpen] = useState(false);
  const [selectedMonthYear, setSelectedMonthYear] = useState(() => {
    const now = new Date();
    // Format as YYYY-MM for input type="month"
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const isSearched = useRef(false);
  const currentPropertyCode = useRef("GIB");

  const { columns, rows, getPropertyLeasesList, getPropertyLeasesExcel, innerLoading, buttonDisabled, setButtonDisabled, open, setOpen, isShowDialog, sortField, sortDirection, setSortField, setSortDirection, editedRow, totalPages, totalRows } = propertiesData();
  // console.log("editedRow", editedRow);

  const onChangeText = useCallback((event) => {
    const { value }  = event.target
    if (value.length < 3) {
      setErrorMessage('Tenant code value must be more equal than 3 characters');
      setErrors(true);
    } else {
      setErrorMessage('');
      setErrors(false);
    }
    setInputValue(value);
  }, []);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenNotification(false);
  }, []);

  const onHandleSubmit = useCallback((event) => {
    event.preventDefault();
    setFilter(inputValue);
    isSearched.current = true;
    setPageCustom(0);
  }, [inputValue]);

  const handleDownloadExcelLeases = async () => {
    try {
      let currentPage = pageCustom + 1;
      if (selectedValue !== currentPropertyCode.current) {
        currentPage = 1;
        currentPropertyCode.current = selectedValue;
        if (pageCustom !== 0) {
          setPageCustom(0);
          return;
        }
      }
      const response = await getPropertyLeasesExcel(currentPage, rowsPerPage, filter, sortField, sortDirection, selectedValue);
      if (response && response.length > 0) {
        setExcelData(response); // Ensure data is set correctly
        setIsExcelPopupOpen(true); // Open the popup for review
      } else {
        console.error("No data received from API");
      }
    } catch (error) {
      console.error("Error fetching Excel data:", error);
    }
  };
  const handleDownloadExcelLeaseInvoices = async () => {
    try {
      setIsExcelPopupOpen(true);
    } catch (error) {
      console.error("Error fetching Excel data:", error);
    }
  };

  const handleCloseExcelPopup = () => {
    setIsExcelPopupOpen(false);
  };

  const handleConfirmDownload = async () => {
    let start_date = selectedMonthYear + "-01"; // Start of the month
    let end_date = new Date(selectedMonthYear + "-01");
    end_date.setMonth(end_date.getMonth() + 1);
    end_date.setDate(0);
    end_date = end_date.toISOString().slice(0, 10);
    const response = await PropertyService.getPropertyLeasesInvoiceList({
      propertyId: selectedValue,
      start_date: start_date,
      end_date: end_date
    }, selectedValue);
    if (response instanceof Blob) {
      const text = await response.text();
      try {
        const json = JSON.parse(text);
        if( json.length === 0 ) {
          // Display notification or message to user
          alert("No Invoices available on ". concat(selectedMonthYear));
          return; // Exit if no data
        }

        let total_rent_amount = 0;
        // Mapping the JSON data to the format required by XLSX
        const mappedData = json.map(({ tenant_code, property_code, total_amount, tenant_name, area_size, remarks, lease_address, period_name }) => {
          let price_per_sqft = "";
          if (area_size && total_amount) {
            price_per_sqft = (total_amount / area_size).toFixed(2); // Calculate price per square foot
          } else {
            price_per_sqft = ""; // Handle case where area_size or total_amount is not available
          }
          total_rent_amount += total_amount; // Accumulate total rent amount
          return {
            "Property Code": property_code,
            "Tenant Code": tenant_code,
            "Period": period_name,
            "Total Amount": total_amount,
            "Tenant Name": tenant_name,
            "Address": lease_address,
            "Area Size": area_size,
            "price per square foot": price_per_sqft,
            "Remarks": remarks
          }
        });

        // Add Total Rent Amount at the end of the data
        // Merge first 3 columns for title row
        mappedData.push({
          "Property Code": "",
          "Tenant Code": "",
          "Period": "Total Amount",
          "Total Amount": total_rent_amount,
          "Tenant Name": "",
          "Address": "",
          "Area Size": "",
          "price per square foot": "",
          "Remarks": ""
        });

        const worksheet = XLSX.utils.json_to_sheet(mappedData); // Convert array of objects to worksheet
        const workbook = XLSX.utils.book_new(); // Create a new workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, "Property Lease Invoices"); // Append the worksheet
        XLSX.writeFile(workbook, "Property-Lease-Invoice-"+selectedMonthYear+".xlsx"); // Write the workbook to a file
        setIsExcelPopupOpen(false); // Close the popup after download
      } catch (e) {
        console.error("Failed to parse JSON:", e, text);
      }
    }
  };

  const onChangeSelect = (event) => {
    const { value, name } = event.target;
    if (name === "property_code") {
      // console.log("value", value);
      setSelectedValue(value);
      if (filter !== "") {
        setFilter('');
        setInputValue('');
      }
    }
  }

  useEffect(() => {
    // console.log("selectedValue", selectedValue);
    // console.log("currentPropertyCode", currentPropertyCode.current);
    let currentPage = pageCustom + 1;
    // If the selected value is different from the current property code, reset the page to 0
    if (selectedValue !== currentPropertyCode.current) {
      currentPage = 1;
      currentPropertyCode.current = selectedValue;
      if (pageCustom !== 0) {
        setPageCustom(0); // Trigger lại useEffect nếu cần
        return; // Ngăn gọi API ngay
      }
    }
    getPropertyLeasesList(currentPage, rowsPerPage, filter, sortField, sortDirection, selectedValue);
  },[selectedValue, pageCustom, rowsPerPage, filter]);

  useEffect(() => {
    // console.log("open", open);
    if (open) {
      document.documentElement.style.overflow = "hidden"; // html
    } else {
      document.documentElement.style.overflow = "";
    }

    return () => {
      // Cleanup nếu component bị unmount
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (isEdited) {
      let currentPage = pageCustom + 1;
      getPropertyLeasesList(currentPage, rowsPerPage, filter, sortField, sortDirection, selectedValue);
      setIsEdited(false);
    }
  }, [isEdited])

  return (
    <DashboardLayout>
      <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    Property Leases
                  </MDTypography>
                </MDBox>
                <Grid container spacing={2} sx={{ padding: "19px 20px"}}>
                  <Grid item xs={9}>
                    <MDBox
                        display="flex"
                        flexDirection="column"
                        gap={3}
                        component="form"
                        role="form"
                        method="POST"
                        onSubmit={onHandleSubmit}
                    >
                        <MDBox
                            display="flex"
                            alignItems="center"
                            gap={2}
                            justifyContent="space-between"
                            flexWrap="nowrap"
                        >
                            <MDInput
                                type="search"
                                label="Filter"
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                sx={{
                                    "& .MuiInput-underline:after": { borderBottomColor: "#388e3c"},
                                    "& .MuiInputLabel-root.Mui-focused": { color: "#388e3c" }
                                }}
                                value={inputValue}
                                onChange={onChangeText}
                                name="tenant_code"
                            />
                            <MDButton
                                variant="gradient"
                                sx={{
                                    padding: "12px 30px",
                                    backgroundColor: "#388e3c",
                                    color: "#ffffff",
                                    borderRadius: "0.2rem",
                                    "&:hover": {
                                    backgroundColor: "#919191",
                                    color: "#fff",
                                    },
                                    "&:disabled": {
                                    background: "#e0e0e0"
                                    }
                                }}
                                disabled={ buttonDisabled }
                                type="submit"
                            >
                                Search
                            </MDButton>
                        </MDBox>
                        <MDBox mb={2}>
                          <FormControl width="50%">
                              <InputLabel
                                  id="property-code-select-label"
                              >
                                  Property code
                              </InputLabel>
                              <Select
                                  labelId="property-code-select-label"
                                  name="property_code"
                                  value={selectedValue}
                                  onChange={onChangeSelect}
                                  displayEmpty
                                  label="Property code"
                                  sx={{
                                      minWidth: 200,
                                      height: '44px', // Set chiều cao cố định
                                  }}
                                  MenuProps={{
                                  disableScrollLock: true, // Ngăn MUI tự động thêm padding-right vào <body>
                                  }}
                              >
                                  <MenuItem value={gibId}>GIB</MenuItem>
                                  <MenuItem value={ylcId}>YLC</MenuItem>
                                  <MenuItem value={yl3Id}>YL3</MenuItem>
                              </Select>
                          </FormControl>
                        </MDBox>
                    </MDBox>
                  </Grid>
                  <Grid item xs={3}>
                    <MDButton
                      variant="gradient"
                      sx={{
                        padding: "12px 30px",
                        backgroundColor: "#388e3c",
                        color: "#ffffff",
                        borderRadius: "0.2rem",
                        "&:hover": {
                          backgroundColor: "#919191",
                          color: "#fff",
                        },
                      }}
                      onClick={handleDownloadExcelLeaseInvoices}
                    >
                      Download Report
                    </MDButton>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox>
                      <DataTable
                          table={{ columns, rows }}
                          isSorted={false}
                          showTotalEntries={false}
                          entriesPerPage={false}
                          isPaginated={true}
                          pageCustom={pageCustom}
                          setPageCustom={setPageCustom}
                          totalPages={totalPages}
                          totalRows={totalRows}
                          rowsPerPage={rowsPerPage}
                          setRowsPerPage={setRowsPerPage}
                          innerLoading={innerLoading}
                          isShow={isShow}
                          noEndBorder
                      />
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        { isShowDialog && (
          <EditLeases
            open={open}
            setOpen={setOpen}
            setOpenNotification={setOpenNotification}
            rowData={editedRow}
            setIsShow={setIsShow}
            setIsEdited={setIsEdited}
            setErrorEdit={setErrorEdit}
          />
        )}
        {isExcelPopupOpen && (
          <MDBox
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#001f3f", // Dark blue background
              color: "#ffffff", // White text color
              zIndex: 1300, // Ensure it appears above other elements
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
              width: "270px", // Set a fixed width
            }}
          >
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" color="black">Excel Report</MDTypography>
                {/* Month/Year select field */}
                <MDBox mt={2} mb={2}>
                  <TextField
                    label="Month / Year"
                    type="month"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={selectedMonthYear}
                    onChange={e => setSelectedMonthYear(e.target.value)}
                  />
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mt={2}>
                  <MDButton variant="outlined" color="error" onClick={handleCloseExcelPopup}>
                    Cancel
                  </MDButton>
                  <MDButton variant="gradient" color="success" onClick={handleConfirmDownload}>
                    Download
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}
        <Snackbar open={openNotification} autoHideDuration={4000} onClose={handleClose}>
          { errorEdit ? (
            <Alert
            onClose={handleClose}
            severity="error"
            variant="filled"
            sx={{ width: '100%', color: '#ffffff' }}
          >
            Update Lease Unsuccessfully !
          </Alert>
          ) : (
            <Alert
              onClose={handleClose}
              severity="success"
              variant="filled"
              sx={{ width: '100%', color: '#ffffff' }}
            >
              Update Lease Successfully !
            </Alert>
          )}
      </Snackbar>
    </DashboardLayout>
  );
}
export default Payments;
